import { Link } from 'react-router';

/**
 * CAB-505: the Terms of Service body, extracted from TermsPage so the same
 * text can render both on the public /legal/terms page and inside the
 * LegalConsentModal (scroll-to-accept gate). Kept in shared/ (not features/)
 * to respect feature-isolation.
 */
export function TermsContent() {
  return (
    <article className="space-y-8 text-sm leading-relaxed">
      <header className="space-y-1">
        <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          General Technologies Inc.
        </p>
        <h1 className="text-foreground text-3xl font-bold">Terms of Service Agreement</h1>
        <p className="text-muted-foreground text-xs italic">Last updated: January 8, 2026</p>
      </header>

      <section className="space-y-4">
        <p>
          This Agreement describes the terms and conditions applicable to the use of the ChartAI
          App, software, content and online services made available through the General Technologies
          website, ChartAI website, and through the ChartAI App (collectively, the
          &quot;Service&quot;) by authorized users.
        </p>
        <p>
          This is a binding agreement between General Technologies. (&quot;us&quot;, &quot;we&quot;)
          and You (&quot;you&quot;, &quot;your&quot;) for the Service. Your access to and use of the
          Service is conditioned on your acceptance of and compliance with these Terms. These Terms
          apply to all visitors, users and others who access or use the Service.
        </p>
        <p>
          This agreement (&quot;Agreement&quot;) is incorporated and made part of the Subscription
          Form (&quot;Subscription&quot;, the &quot;Purchase&quot;) between you and/or your company
          (collectively the &quot;Customer&quot; or &quot;You&quot;, as defined in the Purchase),
          and us, pursuant to which you purchased access to the Service.
        </p>
        <p>
          We may change this Agreement at any time without prior written notice to you. If you use
          the Service after such change you hereby agree to such change. It is your responsibility
          to read the Agreement carefully each time you access the Service. We will provide notice
          that a change has been made to the Agreement.
        </p>
        <p>
          To the extent of any direct conflicts, this Agreement shall take precedence over the
          Subscription Form.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Accounts and Registration
        </h2>
        <p>
          To access the Service, you must register for and hold an account. By creating an account,
          you confirm that you possess the legal right and ability to enter into a legally binding
          agreement with us and agree to use the Service only in accordance with these Terms. As
          part of the registration process, you will identify an administrative user name and
          password for your Company account. You are responsible for keeping this username and
          password secure and are responsible for all use and activity carried out under your
          account. We reserve the right to refuse registration of, or cancel passwords we deem
          inappropriate.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Privacy and Cookies Policy
        </h2>
        <p>
          In the performance of these Terms, each party must comply with applicable privacy laws in
          respect of any personal information, including without limitation PIPEDA, applicable
          provincial private-sector privacy laws (including British Columbia&apos;s PIPA,
          Alberta&apos;s PIPA, and Québec&apos;s Act respecting the protection of personal
          information in the private sector (as amended by Law 25), the General Data Protection
          Regulation (GDPR) in the European Union, and the California Consumer Privacy Act (CCPA) in
          the United States.
        </p>
        <p>
          You agree that our{' '}
          <Link to="/legal/privacy" className="text-primary hover:text-primary/80 underline">
            Privacy Policy
          </Link>{' '}
          which includes our Cookies Policy are made part of the terms of the Purchase for the
          Services and acknowledge having reviewed them prior to using the Services.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Business Associate Agreement (HIPAA)
        </h2>
        <p>
          If Customer is a &apos;Covered Entity&apos; or &apos;Business Associate&apos; under HIPAA
          and Customer&apos;s use of the Service involves Protected Health Information
          (&apos;PHI&apos;), then prior to using the Service to create, receive, maintain, or
          transmit PHI, Customer and General Technologies must enter into General Technologies&apos;
          Business Associate Agreement (&apos;BAA&apos;), Schedule A attached to and forming part of
          this agreement between us. The BAA is incorporated by reference and forms part of these
          Terms when executed by the parties. In the event of any conflict between the BAA and these
          Terms (or the Privacy Policy), the BAA will control with respect to PHI and HIPAA-related
          obligations.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Personal Information Notice
        </h2>
        <p>
          We collect, store, use, and disclose personal information about you and your patients in
          order to provide you with the Service and for other purposes set out in our Privacy
          Policy.
        </p>
        <p>Our Privacy Policy explains:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>
            How we store and use your personal information, and how you may access and correct it.
          </li>
          <li>
            How you can lodge a complaint regarding the handling of your personal information.
          </li>
          <li>How we will handle any complaint.</li>
        </ul>
        <p>
          For further information about our privacy practices, you can view our Privacy Policy or
          contact us at privacy@generaltech.io.
        </p>
        <p>
          By providing your personal information to us, you consent to the collection, use, storage,
          and disclosure of that information as described in these Terms and our Privacy Policy. We
          may disclose personal information only (a) to service providers acting on our behalf under
          written agreements requiring confidentiality, appropriate safeguards, and use solely to
          provide services to us, (b) to parties you authorize (including via integrations), and/or
          (c) as required by law. If you do not provide this information, we will not be able to
          provide the Service to you.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Use of De-Identified Information
        </h2>
        <p>
          We may de-identify personal information, including personal health information, in
          accordance with applicable law. For U.S. HIPAA-regulated information, de-identification
          will be performed in accordance with 45 C.F.R. § 164.514. For GDPR-regulated information,
          information will be treated as anonymized only where it is rendered non-identifiable
          consistent with Recital 26.
        </p>
        <p>
          We may use and disclose information only in de-identified/anonymized form to make relevant
          functionality available to you and as otherwise described in our Privacy Policy, including
          disclosure to contracted service providers operating the Service on our behalf. We will
          take reasonable steps to prevent de-identified/anonymized information from being
          re-identified.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          SaaS Services and Support
        </h2>
        <p>
          Subject to the terms of this Agreement, we will use commercially reasonable efforts to
          provide you the Services and reasonable technical support services related to the
          Services.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Restrictions and Responsibilities
        </h2>
        <p>
          You will only access the Services in compliance with the terms of this Agreement. You will
          use the Services only in compliance with our Policies then in effect and all applicable
          laws and regulations.
        </p>
        <p className="font-semibold">You must:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>
            Receive consent from your patient to share personal and sensitive information with us if
            mandatory in your jurisdiction.
          </li>
          <li>
            Represent and warrant that you have secured all necessary authorizations and current
            consents from your patients to disclose their personal information (including sensitive
            information) to us.
          </li>
          <li>
            Notify us immediately if you become aware of any problem or incident associated with the
            Service that has caused, or could cause, harm to patients or others.
          </li>
          <li>
            Notify us immediately if you become aware of any actual or potential defect in the
            Service or any complaint reported by any person in connection with the Service.
          </li>
        </ul>
        <p>
          Except as otherwise permitted under these Terms, you will not, directly or indirectly:
          reverse engineer, decompile, disassemble or otherwise attempt to discover the source code,
          object code or underlying structure, ideas, know-how or algorithms relevant to the
          Services or any software, documentation or data related to the Services
          (&quot;Software&quot;); modify, translate, or create derivative works based on the
          Services or any Software; use the Services or any Software for timesharing or service
          bureau purposes or otherwise for the benefit of a third party; or remove any proprietary
          notices or labels.
        </p>
        <p>
          You shall be responsible for obtaining and maintaining any equipment and ancillary
          services needed to connect to, access or otherwise use the Services. You shall also be
          responsible for maintaining the security of the Equipment, your account, passwords and
          files, and for all uses of your account or the Equipment with or without your knowledge or
          consent.
        </p>
        <h3 className="text-foreground font-semibold">Third-Party Functionality</h3>
        <p>
          The Service&apos;s functionality may involve the use of software, data, applications,
          services, or content that is provided to us by third parties (&quot;Third-Party
          Functionality&quot;). You agree to comply with any reasonable additional terms notified to
          you by us in respect of the use of Third-Party Functionality. If you do not agree to
          comply with those terms, we may need to terminate your account.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Confidentiality; Proprietary Rights
        </h2>
        <p>
          Each party (the &quot;Receiving Party&quot;) understands that the other party (the
          &quot;Disclosing Party&quot;) has disclosed or may disclose business, technical or
          financial information relating to the Disclosing Party&apos;s business (hereinafter
          referred to as &quot;Proprietary Information&quot;). Our Proprietary Information includes
          non-public information regarding features, functionality, and performance of the Service.
          Your Proprietary Information includes non-public data provided by you to us to enable the
          provision of the Services (&quot;Customer Data&quot;). The Receiving Party agrees: (i) to
          take reasonable precautions to protect such Proprietary Information, and (ii) not to use
          or divulge to any third person any such Proprietary Information, except in performance of
          the Services.
        </p>
        <p>
          You shall own all right, title, and interest in and to the Customer Data. You grant to us
          a non-exclusive, worldwide, perpetual, irrevocable, royalty-free licence to use
          de-identified/anonymized material you provide or otherwise make available to us for the
          purpose of making Service functionality available to you, and as otherwise permitted by
          these Terms.
        </p>
        <p>
          Notwithstanding anything to the contrary, we shall have the right to collect and analyze
          data and other information relating to the provision, use and performance of various
          aspects of the Services and related systems and technologies, and we will be free (during
          and after the Term) to (i) use such information and data to improve and enhance the
          Services and for other development, diagnostic and corrective purposes in connection with
          the Services, and (ii) disclose such data solely in aggregate or other de-identified form
          in connection with its business.
        </p>
        <p>
          To enable us to use your feedback for our business purposes and to improve the Service,
          you agree to grant us a non-exclusive, worldwide, perpetual, irrevocable, royalty-free,
          sublicensable license to use feedback you provide to us.
        </p>
        <p>
          We shall own and retain all right, title and interest in and to (a) the Services and
          Software, all improvements, enhancements or modifications thereto, (b) any software,
          applications, inventions or other technology developed in connection with the
          implementation services or support, and (c) all intellectual property rights related to
          any of the foregoing.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Payment of Fees
        </h2>
        <h3 className="text-foreground font-semibold">Fees</h3>
        <p>
          You agree to pay all fees specified in your Order Form. Unless otherwise stated in this
          Agreement or your Order Form: fees are based on the subscription plan purchased, all
          payments are non-cancellable and non-refundable, and you may not reduce the quantity of
          services during the current subscription term.
        </p>
        <h3 className="text-foreground font-semibold">Invoicing and Payment</h3>
        <p>
          You must provide a valid, up-to-date credit card, purchase order, or another acceptable
          payment method. By submitting your credit card information, you authorise General
          Technologies to charge it for the full amount of your subscription—both for the initial
          term and any renewal periods.
        </p>
        <h3 className="text-foreground font-semibold">Late Payments</h3>
        <p>If payment isn&apos;t received by the due date, General Technologies may:</p>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>
            charge late fees at 1.5% per month on the outstanding balance (or the highest rate
            allowed by law, if lower), and/or
          </li>
          <li>require prepayment or shorter payment terms for future subscriptions or renewals.</li>
        </ul>
        <h3 className="text-foreground font-semibold">
          Service Suspension and Accelerated Payments
        </h3>
        <p>
          If any unpaid amount remains overdue for 30 days—or 10 days in cases of failed credit card
          charges—General Technologies may suspend your access to the services and/or accelerate the
          full balance of all fees due.
        </p>
        <h3 className="text-foreground font-semibold">Payment Disputes</h3>
        <p>
          We won&apos;t enforce late charges or suspend services if you&apos;re disputing a charge
          in good faith and working promptly with us to resolve the issue.
        </p>
        <h3 className="text-foreground font-semibold">Taxes</h3>
        <p>
          All fees are exclusive of taxes, levies, duties, or similar government charges. You are
          responsible for paying all applicable Taxes related to your purchase.
        </p>
        <h3 className="text-foreground font-semibold">Changes to Fees</h3>
        <p>
          We may revise our pricing or introduce new charges with 30 days&apos; notice. Continued
          use of the services after the notice period constitutes your agreement to the updated
          pricing.
        </p>
        <h3 className="text-foreground font-semibold">Billing Issues</h3>
        <p>
          If you believe you were billed incorrectly, notify us within 45 days of the invoice date
          by contacting us at <strong>accounting@generaltech.io</strong>.
        </p>
        <h3 className="text-foreground font-semibold">Subscription Renewals</h3>
        <p>
          Your subscription will automatically renew at the end of each term unless you cancel
          before the renewal date. We do not offer refunds or credits for partial periods, unless
          explicitly stated otherwise in these Terms.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Free Trial Terms
        </h2>
        <p>
          If you register for a free trial, we will provide access to the applicable service(s) at
          no charge until the earlier of: (a) the end of the designated trial period, (b) the start
          date of any paid subscription, or (c) termination of the trial at our sole discretion.
        </p>
        <h3 className="text-foreground font-semibold">Important Notice About Trial Use:</h3>
        <ul className="text-muted-foreground list-disc space-y-1 pl-6">
          <li>
            Some features available in the paid version may be limited or inaccessible during the
            trial.
          </li>
          <li>
            Data entered during the trial may not carry over unless you upgrade to a paid
            subscription.
          </li>
          <li>
            If you switch to a different service tier after the trial, feature availability and data
            access may vary.
          </li>
        </ul>
        <h3 className="text-foreground font-semibold">
          Disclaimer of Warranties &amp; Limitation of Liability During Trial:
        </h3>
        <p>
          During the trial, services are provided strictly &quot;as-is&quot; and without warranty.
          General Technologies assumes no liability or indemnification obligations for the trial
          period, except where such exclusions are not enforceable under applicable law. In such
          cases, liability is limited to a maximum of $100.00.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Term and Termination
        </h2>
        <p>
          We may suspend your access or use of the Service and terminate this Agreement for your
          failure to make payment, for any violation of this Agreement, submission of false
          information, or for offensive or defamatory language submitted to the Service.
        </p>
        <p>
          You may terminate your use of the Service at any time through your account settings or by
          emailing us. Such termination will take effect the day after the last day of your current
          subscription period, and we will not refund any fees that you have already paid.
        </p>
        <p>
          Upon termination, we will retain, return, or delete Customer Data in accordance with our
          Privacy Policy and applicable law. All sections of this Agreement which by their nature
          should survive termination will survive termination.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Fair Use Policy
        </h2>
        <h3 className="text-foreground font-semibold">Storage:</h3>
        <p>
          The services allow you to store customer data. We have made sure that all customers will
          have plenty of storage space when the services are used normally. If we detect that you
          save excessive customer data, then we will contact you to find a solution.
        </p>
        <h3 className="text-foreground font-semibold">Network Traffic and Bandwidth:</h3>
        <p>
          To prevent a negative effect of excessive network traffic on your user experience or that
          of others, we may monitor your network traffic and bandwidth use. If we detect a situation
          that could lead to a decrease in service for customers, we will contact you to discuss the
          situation.
        </p>
        <h3 className="text-foreground font-semibold">Urgent and Extreme Cases:</h3>
        <p>
          In urgent or extreme cases we may stop the services, or temporarily block your access to
          them. We will use reasonable efforts to provide notice prior to doing so. In the event the
          usage of the services for any given individual login of yours exceeds 50 patients per
          week, the Company may charge additional fees.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Warranty and Disclaimer
        </h2>
        <h3 className="text-foreground font-semibold">
          No Professional Medical or Healthcare Advice:
        </h3>
        <p>
          You agree that your use of the Service is solely for the purposes of supporting your
          administrative processes. You must exercise all necessary, and final, professional and
          medical decisions in relation to a patient&apos;s diagnosis, advice, or treatment. The
          Service does not constitute professional medical or healthcare advice, diagnosis or
          recommendation of treatment.
        </p>
        <h3 className="text-foreground font-semibold">Disclaimer:</h3>
        <p>
          We shall use reasonable efforts consistent with prevailing industry standards to maintain
          the Services in a manner which minimizes errors and interruptions. Services may be
          temporarily unavailable for scheduled maintenance or for unscheduled emergency
          maintenance.
        </p>
        <div className="bg-muted rounded-lg p-4 text-xs font-medium uppercase">
          <p>
            HOWEVER, WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR FREE; NOR DO
            WE MAKE ANY WARRANTY AS TO THE RESULTS THAT MAY BE OBTAINED FROM USE OF THE SERVICES.
            EXCEPT AS EXPRESSLY SET FORTH IN THIS SECTION, THE SERVICES ARE PROVIDED &quot;AS
            IS&quot; AND WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED
            TO, IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE AND
            NON-INFRINGEMENT.
          </p>
          <p className="mt-3">
            OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING FROM OR IN
            CONNECTION WITH THIS AGREEMENT WILL NOT EXCEED THE AMOUNTS ACTUALLY PAID TO US BY YOU IN
            THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING YOUR FORMAL WRITTEN NOTICE OF THE
            CLAIM. IN NO EVENT WILL WE BE LIABLE FOR SPECIAL, INCIDENTAL, INDIRECT OR CONSEQUENTIAL
            DAMAGES ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT.
          </p>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          No Class Action or Jury Trial
        </h2>
        <p>
          You agree that, with respect to any dispute with us arising out of or relating to your use
          of the Service or this Agreement: YOU HEREBY GIVE UP YOUR RIGHT TO HAVE A TRIAL BY JURY;
          and YOU HEREBY GIVE UP YOUR RIGHT TO PARTICIPATE AS A MEMBER OF A CLASS OF CLAIMANTS IN
          ANY LAWSUIT INCLUDING BUT NOT LIMITED TO CLASS ACTION LAWSUITS.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Release and Indemnity
        </h2>
        <p>
          By using this Service, you agree to accept such risks and acknowledge that we are not
          responsible for the acts or omissions of our users. You hereby release us from all claims,
          demands, and damages of every kind arising out of or in any way connected with any such
          dispute. You will defend, indemnify and hold us harmless from any claims, actions,
          amounts, fees, damages or costs attributable to your use or access to the Service.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          No Agency or Partnership
        </h2>
        <p>
          The parties to this Agreement are independent contractors. WE ARE NOT AN AGENT, PROMOTER,
          RESELLER, AFFILIATE, PARTNER OR JOINT VENTURER WITH YOU OR ANY OTHER CUSTOMER. We may
          assign any or all of its rights hereunder to any party without your consent. You will not
          assign this Agreement or any of your rights or obligations without our prior written
          consent.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Entire Agreement
        </h2>
        <p>
          This Agreement, together with the Privacy Policy of the Service, constitutes the entire
          agreement between us and you regarding the subject matter of this Agreement, and
          supersedes and replaces all prior or contemporaneous communications whether electronic,
          oral or written between the Service and you with respect to such subject matter.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Governing Law
        </h2>
        <p>
          The laws of the province of British Columbia and the laws of Canada will govern this
          Agreement and the use of the Service. The parties hereby agree to irrevocably attorn to
          the exclusive jurisdiction of the Province of British Columbia for any claim, action or
          proceeding arising out of or related to this Agreement.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
          Miscellaneous
        </h2>
        <p>
          If any portion of this Agreement is deemed unlawful, void or unenforceable by any
          arbitrator or court of competent jurisdiction, this Agreement as a whole shall not be
          deemed unlawful, void or unenforceable, but only that portion that is unlawful, void or
          unenforceable shall be stricken from this Agreement.
        </p>
      </section>

      <hr className="border-border" />

      <section className="space-y-6">
        <header>
          <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
            Schedule A
          </h2>
          <h2 className="text-foreground text-base font-semibold tracking-wide uppercase">
            Business Associate Agreement
          </h2>
        </header>

        <p>
          This Business Associate Agreement (&quot;BAA&quot;) is by and between General Technologies
          Inc., together with its subsidiaries and affiliates, including without limitation those
          providing the ChartAI product and related services (collectively, &quot;Business
          Associate&quot;), and Customer (&quot;Covered Entity&quot;), and is effective as of the
          Effective Date.
        </p>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Recitals</h3>
          <p>
            WHEREAS, pursuant to the applicable agreement between the parties, Business Associate
            will provide certain services to, for, or on behalf of Covered Entity involving the use
            or disclosure of Protected Health Information (&quot;PHI&quot;), and pursuant to such
            Services Agreement, Business Associate may be considered a &quot;business
            associate&quot; of Covered Entity; and
          </p>
          <p>
            WHEREAS, Covered Entity and Business Associate intend to protect the privacy and provide
            for the security of PHI disclosed to Business Associate pursuant to the Services
            Agreement in compliance with HIPAA and the HIPAA Rules, and the Health Information
            Technology for Economic and Clinical Health Act of 2009 (the &quot;HITECH Act&quot;);
            and
          </p>
          <p>
            WHEREAS, the purpose of this BAA is to satisfy certain standards and requirements of the
            HIPAA Rules and the HITECH Act.
          </p>
          <p>
            NOW, THEREFORE, in consideration of the mutual promises below and the exchange of
            information pursuant to this BAA, the parties agree as follows:
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Definitions</h3>
          <p>
            Capitalised terms used but not otherwise defined in this BAA shall have the same meaning
            as set forth in 45 CFR Parts 160, 162 and 164, and the HITECH Act.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-foreground font-semibold">Obligations of Business Associate</h3>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Permitted Uses and Disclosures:</h4>
            <p>
              Business Associate agrees to only use or disclose PHI as necessary to perform the
              services set forth in the Services Agreement, as permitted under this BAA, or as
              Required by Law.
            </p>
            <p>
              Business Associate shall have the right to de-identify PHI, provided that Business
              Associate implements a de-identification process that conforms to the requirements of
              45 C.F.R. § 164.514(a)–(c). Business Associate may use De-identified Data to provide,
              maintain, protect, and improve the Services.
            </p>
            <p>
              Covered Entity retains all right, title, and interest in PHI. Business Associate
              retains all right, title, and interest in its de-identification methods and in
              De-identified Data to the extent created or derived by Business Associate through the
              Services, excluding any rights in Covered Entity&apos;s underlying PHI.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Nondisclosure:</h4>
            <p>
              Business Associate shall not use or further disclose PHI other than as permitted or
              required by this BAA.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Safeguards:</h4>
            <p>
              Business Associate shall use appropriate safeguards to prevent use or disclosure of
              PHI other than as provided for by this BAA. Business Associate shall maintain a
              comprehensive written information privacy and security program that includes
              administrative, technical, and physical safeguards.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Reporting of Disclosures; Mitigation:</h4>
            <p>
              Business Associate shall report to Covered Entity any use or disclosure of PHI not
              provided for by this BAA, including breaches of unsecured PHI, and shall mitigate, to
              the extent practicable, any harmful effect of a use or disclosure of PHI in violation
              of this BAA.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">
              Business Associate&apos;s Agents and Subcontractors:
            </h4>
            <p>
              Business Associate shall ensure that any subcontractors, agents, or other third
              parties to whom it provides PHI agree in writing to the same restrictions, conditions,
              and requirements that apply to Business Associate with respect to such PHI.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Notification of Breach:</h4>
            <p>
              During the Term of this BAA, Business Associate shall notify Covered Entity within ten
              (10) days of Discovery of any Breach of Unsecured PHI.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Minimum Necessary:</h4>
            <p>
              When using, disclosing, or requesting PHI, Business Associate shall limit PHI to the
              minimum necessary to accomplish the intended purpose of the use, disclosure, or
              request.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Obligations of Covered Entity</h3>
          <p>
            Covered Entity shall be responsible for using appropriate safeguards to maintain and
            ensure the confidentiality, privacy, and security of PHI transmitted to Business
            Associate pursuant to this BAA, in accordance with the standards and requirements of
            HIPAA and the HIPAA Rules.
          </p>
          <p>
            Covered Entity shall provide Business Associate with any changes in, or revocation of,
            permission by an Individual to use or disclose PHI, if such changes affect Business
            Associate&apos;s permitted or required uses or disclosures.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-foreground font-semibold">Term and Termination</h3>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Term</h4>
            <p>
              The term of this BAA shall become effective as of the Effective Date and shall
              terminate when all PHI provided by Covered Entity to Business Associate is destroyed
              or returned to Covered Entity. The provisions of this BAA shall survive termination to
              the extent necessary for compliance with HIPAA and the HIPAA Rules.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-foreground font-medium">Material Breach</h4>
            <p>
              A material breach by either party of any provision of this BAA shall constitute a
              material breach of this BAA if such breach is not cured by the breaching party within
              thirty (30) days after notice from the non-breaching party.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Effect of Termination</h3>
          <p>
            Except as required by law, upon termination of this BAA for any reason, Business
            Associate shall return to Covered Entity or destroy all PHI received from Covered Entity
            that Business Associate still maintains in any form, and shall retain no copies of such
            PHI.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Amendment to Comply with Law</h3>
          <p>
            The parties agree to take such action as is necessary to implement the standards and
            requirements of HIPAA, the HIPAA Rules, the HITECH Act, and other applicable laws
            relating to the security or confidentiality of PHI.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">No Third Party Beneficiaries</h3>
          <p>
            Nothing in this BAA is intended to confer upon any person other than Covered Entity,
            Business Associate, and their respective successors and assigns, any rights, remedies,
            obligations, or liabilities whatsoever.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Effect on Services Agreement</h3>
          <p>
            Except as specifically required to implement the purposes of this BAA, all other terms
            of the Services Agreement remain in full force and effect. In the event of any conflict
            between this BAA and the Services Agreement, this BAA will control with respect to PHI
            and HIPAA-related obligations.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-foreground font-semibold">Interpretation</h3>
          <p>
            This BAA shall be interpreted as broadly as necessary to implement and comply with
            HIPAA, the HIPAA Rules, and any other applicable law relating to security and privacy of
            PHI. Any ambiguity in this BAA shall be resolved in favour of a meaning that permits
            Covered Entity to comply with the HIPAA Rules.
          </p>
        </div>
      </section>
    </article>
  );
}
